<script lang="ts">
  import { getContext } from "svelte";
  import Icon from "../../../../common/icons/Icon.svelte";
  import Button from "../../../../common/form/Button.svelte";
  import FormInput from "../../../../common/form/FormInputWithSurroundError.svelte";
  import RadioCheckIndicator from "../../../common/RadioCheckIndicator.svelte";
  import { CREATE_NEW_ACCOUNT_CONTEXT_NAME } from "../../utils";
  import type { CreateNewAccountContext } from "../../type";

  import { createForm } from "felte";
  import * as yup from "yup";
  import { validator } from "@felte/validator-yup";

  const context: CreateNewAccountContext = getContext(
    CREATE_NEW_ACCOUNT_CONTEXT_NAME
  );
  let accountType: string = context.getAccountType();
  let organizationName: string = context.getOrganizationName();
  let organizationErrorMsg: string = "";
  let canSubmit: boolean = true;
  const options = [
    {
      name: "accountType",
      value: "individual",
      icon: "individual",
      label: "Individual",
    },
    {
      name: "accountType",
      value: "organization",
      icon: "organization",
      label: "Organization",
    },
  ];

  const schema = yup.object({
    organizationName: yup
      .string()
      .test(
        "requiredOrganizationName",
        "Please enter your organization name",
        (value) => {
          return accountType === "individual" || !!value;
        }
      ),
  });

  const { form, errors } = createForm({
    extend: validator({ schema }),
    onSubmit: (values) => {
      context.onNext(values);
    },
  });

  function updateErrorMessage() {
    if (accountType === "organization") {
      let errs = $errors;
      const getError = (er: any[]) => (er ? er[0] : "");

      organizationErrorMsg = getError(errs.organizationName);
      canSubmit = !organizationErrorMsg;
    } else {
      canSubmit = true;
    }
  }

  $: $errors, updateErrorMessage();
</script>

<form use:form method="POST">
  <div class="mt-8 flex flex-row items-center gap-2">
    <div class="text-dark-100 font-semibold uppercase tracking-[0.04em]">
      Account Type
    </div>

    <Icon icon="question-mark" size="13px" />
  </div>

  <div class="radio-group" role="radiogroup">
    {#each options as option}
      {@const selected = accountType === option.value}

      <label class:selected>
        <input
          bind:group={accountType}
          type="radio"
          value={option.value}
          name={option.name}
        />

        <div class="content">
          <Icon icon={option.icon} size="19px" />
          <span class="capitalize">{option.label}</span>
        </div>

        <div class="radio-indicator">
          <RadioCheckIndicator active={selected} />
        </div>
      </label>
    {/each}
  </div>

  <div class="w-full h-80 mt-6">
    {#if accountType === "organization"}
      <div class="input-field flex-grow">
        <p class="required">Organization Name</p>
        <div class="input-container input-only">
          <FormInput
            name="organizationName"
            bind:value={organizationName}
            errorMessage={organizationErrorMsg}
            errorIndicatorIconSize="20px"
            type="text"
            spellcheck="false"
            placeholder="Enter Organization Name"
            autocomplete="off"
          />
        </div>
      </div>
    {/if}
  </div>

  <div class="mb-3 text-dark-50">
    *We use <span class="text-dark-200 font-medium italic">Google Cloud</span>.
    If you need a custom region or certain security credentials Contact Us.
  </div>

  <div class="submit-button relative mt-3">
    <Button class="w-full" disabled={!canSubmit}>Continue</Button>
  </div>
</form>

<style lang="postcss">
  .radio-group {
    @apply mt-3 flex flex-row items-center flex-shrink flex-grow-0 gap-3;
    z-index: 0;
  }

  .radio-group label {
    @apply relative p-0.5 rounded cursor-pointer;
    height: 52px;
    width: 240px;
  }

  .radio-group label::before {
    @apply absolute left-0 right-0 top-0 bottom-0 rounded border border-solid border-light-100 pointer-events-none;
    content: "";
  }

  .radio-group label.selected::before {
    @apply border-0;
  }

  .radio-group label.selected {
    background: linear-gradient(92.69deg, #3bc7ff 0%, #5f89ff 100%);
    box-shadow: 1px 2px 12px rgba(55, 84, 170, 0.2);
  }

  .radio-group label .content {
    @apply w-full h-full flex flex-row items-center gap-3 px-[18px] rounded-sm bg-white;
    color: #d3dbe0;
  }

  @media (max-width: 500px) {
    .radio-group {
      @apply flex flex-col items-start flex-shrink flex-grow-0 gap-3;
    }
  }

  .radio-group label.selected .content {
    @apply text-primary-indigo-blue;
  }

  .radio-group label .content span {
    @apply text-dark-50;
  }

  .radio-group label.selected .content span {
    @apply text-dark-200 font-medium;
  }

  .radio-group input {
    @apply absolute w-0 h-0 opacity-0;
  }

  .radio-group label .radio-indicator {
    @apply absolute top-2 right-2;
    left: unset;
  }

  /* input field */
  .input-field .input-container {
    max-width: 320px;
  }

  .input-container.input-only :global(input) {
    padding: 14px 44px 14px 16px !important;
  }

  .input-field .input-container :global(input::placeholder) {
    @apply font-light;
  }
</style>
